import { z } from "zod"

/** The largest value the settings' INTEGER columns can hold. */
export const SIMULATION_SETTINGS_MAX = 2147483647

const percent = z.number().int().min(0).max(100)
const kronor = z.number().int().min(0).max(SIMULATION_SETTINGS_MAX)

/**
 * The store's numbers that Besiktningskalkyl and Priskalkyl share. A null
 * field means the calculators use what Julo derives from the store's data.
 */
export const SimulationSettingsSchema = z.object({
  /** Finished jobs per year. */
  jobsPerYear: z.number().int().min(0).max(SIMULATION_SETTINGS_MAX).nullable(),
  hourlyPrice: kronor.nullable(),
  laborSharePercent: percent.nullable(),
  productMarginPercent: percent.nullable(),
  /** Salaries per month, employer contributions included. */
  salaryCostPerMonth: kronor,
  rentPerMonth: kronor,
  /** Other fixed costs per month: insurance, electricity, systems. */
  otherCostsPerMonth: kronor,
})

export type SimulationSettings = z.infer<typeof SimulationSettingsSchema>

/** What the calculators use for the nullable settings when left empty. */
export interface SimulationSettingsFromData {
  jobsPerYear: number
  hourlyPrice: number
  laborSharePercent: number
  productMarginPercent: number
}

export interface SimulationSettingsResponse {
  settings: SimulationSettings
  fromData: SimulationSettingsFromData
  /**
   * When the labor share and product margin in fromData were last computed
   * from the store's data, or null if never: then they are the defaults.
   */
  profitSharesComputedAt: Date | null
}

/** The settings of a store that hasn't saved any, matching the DB defaults. */
export const DEFAULT_SIMULATION_SETTINGS: SimulationSettings = {
  jobsPerYear: null,
  hourlyPrice: null,
  laborSharePercent: null,
  productMarginPercent: null,
  salaryCostPerMonth: 71000,
  rentPerMonth: 5000,
  otherCostsPerMonth: 5000,
}
